import React, { useState, useEffect } from "react";
import ModalBox from "../../components/modal/ModalBox";
import useButtonLoader from "../../hooks/UseButtonLoader";
import { Input } from "../../components/Form";
import { requestAcceptRejectApi, updateRequestDetailsApi } from "../../services/requestManagement";
import { toastMessage } from "../../utils/toastMessage";
import PhoneInput from "react-phone-input-2";
import { dateFormat, timeFormat } from "../../utils/dateFormat";
import {
  fieldsDiffer,
  getApproveStatusLabel,
  getRequestKind,
  getRequestTypeLabel,
  hasCurrentData,
  isEditAccessRequest,
  normalizeRequestType,
  resolveBankImageUrl,
  resolvePanImageUrl,
} from "../../utils/requestChangeControl";

const changedFieldStyle = {
  background: "#fff7e6",
  borderColor: "#faad14",
};

const badgeStyle = (bg, color) => ({
  display: "inline-flex",
  alignItems: "center",
  padding: "2px 10px",
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 600,
  background: bg,
  color,
  marginRight: 8,
});

const AttachmentImage = ({ src, alt }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) {
    return (
      <div
        className="text-muted"
        style={{ color: "#888", marginTop: 8, padding: 16, border: "1px dashed #ddd", borderRadius: 6 }}
      >
        No image available
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      style={{
        maxWidth: "100%",
        height: "auto",
        borderRadius: 6,
        border: "1px solid #eee",
        marginTop: 8,
        cursor: "pointer",
        transition: "transform 0.2s ease",
      }}
      onClick={() => window.open(src, "_blank")}
      onMouseOver={(e) => (e.target.style.transform = "scale(1.02)")}
      onMouseOut={(e) => (e.target.style.transform = "scale(1)")}
      onError={() => setFailed(true)}
    />
  );
};

const RequestDetail = ({
  viewModal,
  setViewModal,
  requestId,
  getRequestList,
  requestDetail,
  newData,
  currentData,
  selectedRequestDetail,
  requestType,
  requestdetails,
  detailApiResult,
}) => {
  const resolvedType = normalizeRequestType(
    requestdetails?.requestType || newData?.requestType || requestType
  );
  const kind = getRequestKind({
    requestType: resolvedType,
    currentData,
    newData,
    approveStatus: requestId?.approveStatus,
    source: requestdetails?.source || detailApiResult?.source,
    review: requestdetails?.review || detailApiResult?.review,
  });
  const showPrevious = hasCurrentData(currentData);
  const isAccessRequest = isEditAccessRequest(resolvedType);
  const isPending = Number(requestId?.approveStatus) === 1;
  const currentBankImage = resolveBankImageUrl(currentData);
  const newBankImage =
    resolveBankImageUrl(newData) ||
    (Number(requestId?.approveStatus) === 2
      ? resolveBankImageUrl(detailApiResult?.newData || requestdetails?.newData)
      : "");
  const currentPanImage = resolvePanImageUrl(currentData);
  const newPanImage =
    resolvePanImageUrl(newData) ||
    (Number(requestId?.approveStatus) === 2
      ? resolvePanImageUrl(detailApiResult?.newData || requestdetails?.newData)
      : "");

  const [buttonref, buttonLoader] = useButtonLoader("Save");
  const [formData, setFormData] = useState({});
  const [imageFiles, setImageFiles] = useState({});
  const [imagePreviews, setImagePreviews] = useState({});

  useEffect(() => {
    if (newData) {
      setFormData({
        accountNumber: newData?.accountNumber || "",
        bankName: newData?.bankName || "",
        ifsc: newData?.ifsc || "",
        bankHolderName: newData?.bankHolderName || "",
        panNumber: newData?.panNumber || "",
        mobile: newData?.mobile || "",
        countryCode: newData?.countryCode || "",
        price: newData?.price || "",
      });
      setImagePreviews({
        attachment: resolveBankImageUrl(newData),
        panImage: resolvePanImageUrl(newData),
      });
    }
  }, [newData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneChange = (phone, country) => {
    const phoneWithoutCountryCode = phone.replace(country.dialCode, "");
    setFormData((prev) => ({
      ...prev,
      countryCode: `+${country.dialCode}`,
      mobile: phoneWithoutCountryCode,
    }));
  };

  const handleImageChange = (e, imageType) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toastMessage("Please select a valid image file", "error");
        return;
      }

      setImageFiles((prev) => ({
        ...prev,
        [imageType]: file,
      }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews((prev) => ({
          ...prev,
          [imageType]: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateRequest = async () => {
    try {
      buttonLoader(true);
      const formDataToSend = new FormData();

      formDataToSend.append("requestId", requestId?.requestId);
      formDataToSend.append("requestType", resolvedType === "PAN" ? "PANCARD" : resolvedType);

      if (resolvedType === "BANK") {
        formDataToSend.append("accountNumber", formData.accountNumber || "");
        formDataToSend.append("bankName", formData.bankName || "");
        formDataToSend.append("ifsc", formData.ifsc || "");
        formDataToSend.append("bankHolderName", formData.bankHolderName || "");
        if (imageFiles.attachment) {
          formDataToSend.append("attachment", imageFiles.attachment);
        }
      } else if (resolvedType === "PANCARD") {
        formDataToSend.append("panNumber", formData.panNumber || "");
        if (imageFiles.panImage) {
          formDataToSend.append("panImage", imageFiles.panImage);
        }
      } else if (resolvedType === "PHONE") {
        formDataToSend.append("mobile", formData.mobile || "");
        formDataToSend.append("countryCode", formData.countryCode || "");
      } else if (resolvedType === "PRICE") {
        formDataToSend.append("price", formData.price || "");
      } else {
        toastMessage("This request type cannot be edited from admin", "error", "updateRequest");
        return;
      }

      const res = await updateRequestDetailsApi(formDataToSend);
      if (res?.data?.code === 200) {
        toastMessage(res?.data?.message || "Request details updated successfully", "success", "updateRequest");
        setViewModal(false);
        getRequestList();
      } else {
        toastMessage(res?.data?.message || "Failed to update request details", "error", "updateRequest");
      }
    } catch (e) {
      console.log(e.message);
      toastMessage("An error occurred while updating request details", "error", "updateRequest");
    } finally {
      buttonLoader(false);
    }
  };

  const accept_reject_handler = async (data) => {
    if (!isPending) return;
    try {
      buttonLoader(true);
      const res = await requestAcceptRejectApi({
        approveStatus: data,
        requestId: requestId.requestId,
      });
      if (res?.data?.code === 200) {
        toastMessage(res?.data?.message, "success", "acceptReject");
        setViewModal(false);
        getRequestList();
      } else {
        toastMessage(res?.data?.message, "error", "acceptReject");
      }
    } catch (e) {
      console.log(e.message);
      toastMessage(e?.message || "Failed to update request status", "error", "acceptReject");
    } finally {
      buttonLoader(false);
    }
  };

  const highlight = (field, currentValue) => {
    if (!showPrevious) return undefined;
    return fieldsDiffer(formData[field] || newData?.[field], currentValue) ? changedFieldStyle : undefined;
  };

  const consultantName = requestdetails?.consName || requestdetails?.accountName || currentData?.consName || "---";
  const consultantPhone = requestdetails?.phone || requestdetails?.mobile || "---";
  const consultantEmail = requestdetails?.email || "---";
  const createdAt = requestdetails?.createdAt;
  const review = requestdetails?.review || requestdetails?.remark || "";
  const canSave =
    !isAccessRequest &&
    (resolvedType === "BANK" || resolvedType === "PANCARD" || resolvedType === "PHONE" || resolvedType === "PRICE");

  const typeBadgeColors = {
    BANK: ["#e6f4ff", "#1677ff"],
    BANK_EDIT: ["#fff7e6", "#d48806"],
    PANCARD: ["#f9f0ff", "#722ed1"],
    PAN_EDIT: ["#fff0f6", "#c41d7f"],
  };
  const [typeBg, typeColor] = typeBadgeColors[resolvedType] || ["#f5f5f5", "#555"];
  const kindColors = {
    access: ["#fff1f0", "#cf1322"],
    update: ["#e6fffb", "#08979c"],
    first: ["#f6ffed", "#389e0d"],
    admin: ["#e6f4ff", "#0958d9"],
  };
  const [kindBg, kindColor] = kindColors[kind.kind] || ["#f5f5f5", "#555"];

  const modalTitle = isAccessRequest
    ? resolvedType === "BANK_EDIT"
      ? "Consultant is requesting permission to update bank details"
      : "Consultant is requesting permission to update PAN card"
    : `Update ${getRequestTypeLabel(resolvedType)}`;

  const approveLabel = isAccessRequest ? "Grant edit access" : "Accept";
  const rejectLabel = isAccessRequest ? "Reject access" : "Reject";

  return (
    <ModalBox
      status={viewModal}
      onClose={() => {
        setViewModal(false);
      }}
      title={modalTitle}
      closeIcon
      size={"md"}
      custom
      customizedFooter={
        <div className=" model_custom_footer ">
          {isPending && (
            <>
              <button
                className="btn_size mx-4"
                onClick={() => {
                  accept_reject_handler(2);
                }}
              >
                {approveLabel}
              </button>
              <button
                className="btn_size mx-4"
                onClick={() => {
                  accept_reject_handler(3);
                }}
              >
                {rejectLabel}
              </button>
            </>
          )}
          {canSave && (
            <button
              className="btn_size mx-4"
              ref={buttonref}
              onClick={handleUpdateRequest}
              style={{ backgroundColor: "#1976d2", color: "white" }}
            >
              Save Changes
            </button>
          )}
        </div>
      }
    >
      <div
        style={{
          marginBottom: 16,
          padding: 12,
          background: "#fafafa",
          border: "1px solid #eee",
          borderRadius: 8,
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
          <span style={badgeStyle(typeBg, typeColor)}>{getRequestTypeLabel(resolvedType)}</span>
          {kind.kindLabel ? <span style={badgeStyle(kindBg, kindColor)}>{kind.kindLabel}</span> : null}
          <span
            style={badgeStyle(
              Number(requestId?.approveStatus) === 2
                ? "#f6ffed"
                : Number(requestId?.approveStatus) === 3
                ? "#fff1f0"
                : "#fffbe6",
              Number(requestId?.approveStatus) === 2
                ? "#389e0d"
                : Number(requestId?.approveStatus) === 3
                ? "#cf1322"
                : "#d48806"
            )}
          >
            {getApproveStatusLabel(requestId?.approveStatus)}
          </span>
        </div>
        <div style={{ fontWeight: 700, fontSize: 16 }}>{consultantName}</div>
        <div style={{ color: "#666", marginTop: 4 }}>
          {consultantPhone} · {consultantEmail}
        </div>
        {createdAt ? (
          <div style={{ color: "#888", marginTop: 4, fontSize: 13 }}>
            Created {dateFormat(createdAt)}
            {timeFormat(createdAt) ? `, ${timeFormat(createdAt)}` : ""}
          </div>
        ) : null}
        {review ? (
          <div style={{ marginTop: 8, fontSize: 13 }}>
            <strong>Admin remark:</strong> {review}
          </div>
        ) : null}
      </div>

      {resolvedType === "BANK" && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "24px" }}>
          {showPrevious && (
            <div style={{ flex: 1, minWidth: 250 }}>
              <h6>Previous / current approved details</h6>
              <div className="form-group mb-2 mt-4">
                <Input
                  type="text"
                  name="currentBankHolderName"
                  value={currentData?.bankHolderName || ""}
                  placeholder="Enter Bank Holder Name"
                  className="form-control"
                  label={"Account holder name"}
                  disabled
                />
              </div>
              <div className="form-group mb-2">
                <Input
                  type="text"
                  name="currentAccountNumber"
                  value={currentData?.accountNumber || ""}
                  placeholder="Enter Account Number"
                  className="form-control"
                  label={"Account Number"}
                  disabled
                />
              </div>
              <div className="form-group mb-2">
                <Input
                  type="text"
                  name="currentBankName"
                  value={currentData?.bankName || ""}
                  placeholder="Enter Bank Name"
                  className="form-control"
                  label={"Bank Name"}
                  disabled
                />
              </div>
              <div className="form-group mb-2">
                <Input
                  type="text"
                  name="currentIfsc"
                  value={currentData?.ifsc || ""}
                  placeholder="Enter IFSC"
                  className="form-control"
                  label={"IFSC"}
                  disabled
                />
              </div>
              <div className="form-group mb-2">
                <label className="my-1 text-dark" style={{ fontWeight: 500 }}>
                  Attachment
                </label>
                <AttachmentImage src={currentBankImage} alt="Current Bank Attachment" />
              </div>
            </div>
          )}
          <div style={{ flex: 1, minWidth: 250 }}>
            <h6>{showPrevious ? "New submitted details" : "Submitted bank details"}</h6>
            <div className="form-group mb-2 mt-4">
              <Input
                type="text"
                name="bankHolderName"
                value={formData.bankHolderName || ""}
                onChange={handleInputChange}
                placeholder="Enter Bank Holder Name"
                className="form-control"
                label={"Account holder name"}
                maxLength={50}
                style={highlight("bankHolderName", currentData?.bankHolderName)}
              />
            </div>
            <div className="form-group mb-2">
              <Input
                type="text"
                name="accountNumber"
                value={formData.accountNumber || ""}
                onChange={handleInputChange}
                placeholder="Enter Account Number"
                className="form-control"
                label={"Account Number"}
                maxLength={20}
                style={highlight("accountNumber", currentData?.accountNumber)}
              />
            </div>
            <div className="form-group mb-2">
              <Input
                type="text"
                name="bankName"
                value={formData.bankName || ""}
                onChange={handleInputChange}
                placeholder="Enter Bank Name"
                className="form-control"
                label={"Bank Name"}
                maxLength={20}
                style={highlight("bankName", currentData?.bankName)}
              />
            </div>
            <div className="form-group mb-2">
              <Input
                type="text"
                name="ifsc"
                value={formData.ifsc || ""}
                onChange={handleInputChange}
                placeholder="Enter IFSC"
                className="form-control"
                label={"IFSC"}
                maxLength={20}
                style={highlight("ifsc", currentData?.ifsc)}
              />
            </div>
            <div className="form-group mb-2">
              <label className="my-1 text-dark" style={{ fontWeight: 500 }}>
                Bank Attachment
              </label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageChange(e, "attachment")}
                className="form-control"
                label={"Upload New Attachment"}
              />
              <AttachmentImage
                src={imagePreviews.attachment || newBankImage}
                alt="Bank Attachment Preview"
              />
            </div>
          </div>
        </div>
      )}

      {resolvedType === "BANK_EDIT" && (
        <div>
          <p style={{ marginBottom: 16 }}>
            {newData?.reason || "Request to update bank details"}
          </p>
          <h6>Current approved bank details</h6>
          <div className="form-group mb-2 mt-4">
            <Input
              type="text"
              value={currentData?.bankHolderName || ""}
              className="form-control"
              label={"Account holder name"}
              disabled
            />
          </div>
          <div className="form-group mb-2">
            <Input
              type="text"
              value={currentData?.accountNumber || ""}
              className="form-control"
              label={"Account Number"}
              disabled
            />
          </div>
          <div className="form-group mb-2">
            <Input
              type="text"
              value={currentData?.bankName || ""}
              className="form-control"
              label={"Bank Name"}
              disabled
            />
          </div>
          <div className="form-group mb-2">
            <Input
              type="text"
              value={currentData?.ifsc || ""}
              className="form-control"
              label={"IFSC"}
              disabled
            />
          </div>
          <div className="form-group mb-2">
            <label className="my-1 text-dark" style={{ fontWeight: 500 }}>
              Attachment
            </label>
            <AttachmentImage src={currentBankImage} alt="Current Bank Attachment" />
          </div>
        </div>
      )}

      {resolvedType === "PHONE" && (
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          <div style={{ display: "flex", flexDirection: "row", width: "100%", marginBottom: 12 }}>
            <div style={{ width: "50%", fontWeight: 600, color: "#1976d2", fontSize: 16 }}>
              Current Secondary Phone Details
            </div>
            <div style={{ width: "50%", fontWeight: 600, color: "#388e3c", fontSize: 16 }}>
              New Secondary Phone Details
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "row", width: "100%", marginBottom: 16 }}>
            <div style={{ width: "50%", paddingRight: 8 }}>
              <label className="my-1 text-dark" style={{ fontWeight: 500 }}>
                Mobile Number
              </label>
              <PhoneInput
                country={"in"}
                value={`${newData?.countryCode || ""}${requestdetails?.phone || ""}`}
                enableSearch={false}
                placeholder={"Enter Mobile Number"}
                countryCodeEditable={false}
                disabled={true}
              />
            </div>
            <div style={{ width: "50%", paddingLeft: 8 }}>
              <label className="my-1 text-dark" style={{ fontWeight: 500 }}>
                Mobile Number
              </label>
              <PhoneInput
                country={"in"}
                value={`${formData.countryCode || newData?.countryCode || "+91"}${formData.mobile || newData?.mobile || ""}`}
                onChange={handlePhoneChange}
                enableSearch={false}
                placeholder={"Enter Mobile Number"}
                countryCodeEditable={true}
              />
            </div>
          </div>
        </div>
      )}

      {resolvedType === "PANCARD" && (
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          <div style={{ display: "flex", flexDirection: "row", width: "100%", marginBottom: 12 }}>
            {showPrevious && (
              <div style={{ width: "50%", fontWeight: 600, color: "#1976d2", fontSize: 16 }}>
                Previous PAN
              </div>
            )}
            <div style={{ width: showPrevious ? "50%" : "100%", fontWeight: 600, color: "#388e3c", fontSize: 16 }}>
              {showPrevious ? "New PAN" : "Submitted PAN"}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "row", width: "100%", marginBottom: 16 }}>
            {showPrevious && (
              <div style={{ width: "50%", paddingRight: 8 }}>
                <Input
                  type="text"
                  value={currentData?.panNumber || ""}
                  label="PAN Number"
                  disabled
                />
              </div>
            )}
            <div style={{ width: showPrevious ? "50%" : "100%", paddingLeft: showPrevious ? 8 : 0 }}>
              <Input
                type="text"
                name="panNumber"
                value={formData.panNumber || ""}
                onChange={handleInputChange}
                label="PAN Number"
                style={highlight("panNumber", currentData?.panNumber)}
              />
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "row", width: "100%", alignItems: "flex-start" }}>
            {showPrevious && (
              <div style={{ width: "50%", paddingRight: 8 }}>
                <label className="my-1 text-dark" style={{ fontWeight: 500 }}>
                  PAN Image
                </label>
                <AttachmentImage src={currentPanImage} alt="Current PAN" />
              </div>
            )}
            <div style={{ width: showPrevious ? "50%" : "100%", paddingLeft: showPrevious ? 8 : 0 }}>
              <label className="my-1 text-dark" style={{ fontWeight: 500 }}>
                PAN Image
              </label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageChange(e, "panImage")}
                className="form-control"
                label={"Upload New PAN Image"}
              />
              <AttachmentImage src={imagePreviews.panImage || newPanImage} alt="PAN Image Preview" />
            </div>
          </div>
        </div>
      )}

      {resolvedType === "PAN_EDIT" && (
        <div>
          <p style={{ marginBottom: 16 }}>
            {newData?.reason || "Request to update PAN card"}
          </p>
          <h6>Current approved PAN</h6>
          <div className="form-group mb-2 mt-4">
            <Input
              type="text"
              value={currentData?.panNumber || ""}
              label="PAN Number"
              disabled
            />
          </div>
          <div className="form-group mb-2">
            <label className="my-1 text-dark" style={{ fontWeight: 500 }}>
              PAN Image
            </label>
            <AttachmentImage src={currentPanImage} alt="Current PAN" />
          </div>
        </div>
      )}

      {resolvedType === "PRICE" && (
        <div className="row">
          <div className="col-6">
            <h6 className="text-primary">Current Price</h6>
          </div>
          <div className="col-6">
            <h6 className="text-success">New Price</h6>
          </div>
          <div className="col-6 mt-4">
            <div className="form-group mb-2">
              <Input
                type="text"
                name="price"
                value={currentData?.price || ""}
                className="form-control"
                label={"Current Price"}
                disabled
              />
            </div>
          </div>
          <div className="col-6 mt-4">
            <div className="form-group mb-2">
              <Input
                type="text"
                name="price"
                value={formData.price || ""}
                onChange={handleInputChange}
                className="form-control"
                label={"New Price"}
              />
            </div>
          </div>
        </div>
      )}

      {resolvedType === "FLAG" && (
        <>
          <div className="form-group mb-2">
            <Input
              type="text"
              name="consultantName"
              value={currentData?.consName || "---"}
              className="form-control"
              label={"Consultant Name"}
              disabled
            />
          </div>
          <div className="form-group mb-2">
            <Input
              type="text"
              name="userName"
              value={currentData?.userName || "---"}
              className="form-control"
              label={"User Name"}
              disabled
            />
          </div>
          <div className="form-group mb-2">
            <Input
              type="text"
              name="review"
              value={currentData?.review}
              className="form-control"
              label={"Review"}
              disabled
            />
          </div>
          <div className="form-group my-2">
            <Input
              type="text"
              name="reason"
              value={currentData?.flagReason}
              disabled
              className="form-control"
              label={"Reason"}
            />
          </div>
        </>
      )}
    </ModalBox>
  );
};

export default RequestDetail;
